/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// @sfdc-extension-line SFDC_EXT_BRD_REGISTRATION_FIELDS (adds useRef for full-form validity gating)
import { useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Button } from '@/components/ui/button';
import { PasswordRequirement } from '@/components/password-requirements';
import { usePasswordValidation } from '@/hooks/use-password-validation';
// @sfdc-extension-line SFDC_EXT_BRD_REGISTRATION_FIELDS
import { useNativeFormValidity } from '@/extensions/brd-registration-fields/hooks/use-native-form-validity';
import { type SignupFormProps } from './types';
import { useTranslation } from 'react-i18next';
import UiTargetSmokeTest_GenericMarker from '@/extensions/ui-target-smoke-test/components/generic-marker';
import BrdRegistrationFields_RegistrationFields from '@/extensions/brd-registration-fields/components/registration-fields';
import BrdMarketingConsent_RegistrationMarketingConsentTarget from '@/extensions/brd-marketing-consent/components/target/registration-marketing-consent-target';
export function SignupForm({
  error,
  isPasswordless = false,
  initialEmail
}: SignupFormProps) {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const {
    password,
    confirmPassword,
    showPasswordMismatch,
    handlePasswordChange,
    handleConfirmPasswordChange,
    isFormValid
  } = usePasswordValidation();
  const {
    t
  } = useTranslation('signup');

  // @sfdc-extension-block-start SFDC_EXT_BRD_REGISTRATION_FIELDS
  // @sfdc-extension-block-start SFDC_EXT_BRD_MARKETING_CONSENT
  // Gate the submit button on the FULL native form validity (all `required` fields incl. the
  // Boardriders profile fields and the marketing-consent checkbox injected via UITargets),
  // not just the password fields. Uncontrolled extension inputs don't trigger React renders,
  // so we recompute checkValidity() on every input/change event from the enclosing <form>.
  const containerRef = useRef<HTMLDivElement>(null);
  const allRequiredValid = useNativeFormValidity(containerRef, 'closest-form');
  // @sfdc-extension-block-end SFDC_EXT_BRD_MARKETING_CONSENT
  // @sfdc-extension-block-end SFDC_EXT_BRD_REGISTRATION_FIELDS

  // Shows password fields if passwordless is disabled or the user has clicked the "Create account with password" button
  const showPasswordFields = !isPasswordless || showPasswordForm;
  // Only password fields are checked for validity. When passwordless, form is always valid.
  const submitDisabled = showPasswordFields && !isFormValid ||
  // @sfdc-extension-line SFDC_EXT_BRD_REGISTRATION_FIELDS (also gates on consent checkbox)
  !allRequiredValid;
  return <>
            {error && <div className="mb-4 p-3 rounded-ui bg-destructive/10 border border-destructive/20">
                    <p className="text-sm text-destructive">{error}</p>
                </div>}

            {/* @sfdc-extension-line SFDC_EXT_BRD_REGISTRATION_FIELDS (ref for full-form validity gating) */}
            <div className="space-y-6" ref={containerRef}>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-foreground">
                            {t('form.firstNameLabel')}
                        </label>
                        <Input id="firstName" name="firstName" type="text" autoComplete="given-name" required className="mt-1" placeholder={t('form.firstNamePlaceholder')} />
                    </div>
                    <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-foreground">
                            {t('form.lastNameLabel')}
                        </label>
                        <Input id="lastName" name="lastName" type="text" autoComplete="family-name" required className="mt-1" placeholder={t('form.lastNamePlaceholder')} />
                    </div>
                </div>

                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground">
                        {t('form.emailLabel')}
                    </label>
                    <Input id="email" name="email" type="email" autoComplete="email" required className="mt-1" placeholder={t('form.emailPlaceholder')} defaultValue={initialEmail} />
                </div>

                {showPasswordFields && <>
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-foreground">
                                {t('form.passwordLabel')}
                            </label>
                            <PasswordInput id="password" name="password" autoComplete="new-password" required value={password} onChange={handlePasswordChange} className="mt-1" placeholder={t('form.passwordPlaceholder')} showLabel={t('form.showPassword')} hideLabel={t('form.hidePassword')} />
                            <PasswordRequirement password={password} />
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground">
                                {t('form.confirmPasswordLabel')}
                            </label>
                            <PasswordInput id="confirmPassword" name="confirmPassword" autoComplete="new-password" required value={confirmPassword} onChange={handleConfirmPasswordChange} className="mt-1" aria-invalid={showPasswordMismatch && confirmPassword ? true : undefined} placeholder={t('form.confirmPasswordPlaceholder')} showLabel={t('form.showPassword')} hideLabel={t('form.hidePassword')} />
                            {showPasswordMismatch && confirmPassword && <p className="mt-1 text-sm text-destructive">{t('passwordsDoNotMatch')}</p>}
                        </div>
                    </>}

                {isPasswordless && <input type="hidden" name="registrationMode" value={showPasswordForm ? 'password' : 'passwordless'} />}

                <UiTargetSmokeTest_GenericMarker />
                <UiTargetSmokeTest_GenericMarker />
                <><BrdRegistrationFields_RegistrationFields /><UiTargetSmokeTest_GenericMarker /></>
                <><BrdMarketingConsent_RegistrationMarketingConsentTarget /><UiTargetSmokeTest_GenericMarker /></>
                <UiTargetSmokeTest_GenericMarker />

                <div>
                    <Button type="submit" disabled={submitDisabled} className="w-full" variant={submitDisabled ? 'secondary' : 'default'}>
                        {showPasswordFields ? t('form.createAccountButton') : t('form.continueButton')}
                    </Button>
                </div>

                {isPasswordless && <div className="space-y-3">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-border/60" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-background px-2 text-muted-foreground">
                                    {t('form.orSeparator')}
                                </span>
                            </div>
                        </div>

                        <Button type="button" variant="outline" className="w-full" onClick={() => setShowPasswordForm(!showPasswordForm)}>
                            {showPasswordForm ? t('form.continueWithoutPassword') : t('form.createAccountWithPassword')}
                        </Button>
                    </div>}
            </div>
        </>;
}